import { memo, useCallback, type ChangeEvent } from "react";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { PubmedQueryText } from "../../data-display/PubmedQueryText";

const QueryField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": { fontFamily: "monospace" },
  // The text is drawn by the highlight layer; the textarea only supplies the caret and selection.
  "& textarea": { color: "transparent", caretColor: theme.palette.text.primary },
}));

/** Sits over the textarea, inside the input's own padding, and lets every pointer event through. */
const HighlightLayer = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  padding: "inherit",
  pointerEvents: "none",
  whiteSpace: "pre-wrap",
  overflowWrap: "break-word",
  color: theme.palette.text.primary,
}));

export interface PubmedQueryInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  minRows?: number;
  error?: boolean;
  helperText?: string;
}

/** A multi-line text field for an Entrez (PubMed / PMC) query that colours the query as it is
 *  typed — operators, field tags, phrases, and anything left unclosed. It is a plain textarea
 *  underneath, so typing, selection, undo and paste behave natively. */
export const PubmedQueryInput = memo(function PubmedQueryInput({
  label,
  value,
  onChange,
  minRows = 3,
  error,
  helperText,
}: PubmedQueryInputProps) {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    [onChange]
  );

  return (
    <QueryField
      label={label}
      value={value}
      onChange={handleChange}
      multiline
      minRows={minRows}
      fullWidth
      size="small"
      error={error}
      helperText={helperText}
      slotProps={{
        htmlInput: { spellCheck: false },
        input: {
          endAdornment: (
            <HighlightLayer aria-hidden>
              <PubmedQueryText query={value} />
            </HighlightLayer>
          ),
        },
      }}
    />
  );
});
